import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { buildPageTitle } from '../../../shared/constants/site-meta';

/**
 * Static, pixel-perfect country page benchmark at `/country1`.
 *
 * Visual benchmark only — not wired to CMS, APIs, or dynamic section renderer.
 * Styling is scoped via `country-` classes in `src/styles/pages/_country1.scss`.
 */
@Component({
  selector: 'app-country1-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './country1-page.component.html',
})
export class Country1PageComponent implements OnInit {
  private readonly title = inject(Title);

  readonly navLinks = [
    { label: 'Countries', href: '#why' },
    { label: 'Services', href: '#costs' },
    { label: 'Resources', href: '#scholarships' },
    { label: 'About Us', href: '#inquiry' },
  ];

  readonly stats = [
    { value: '€0', label: 'Tuition Fees', icon: 'wallet' },
    { value: '18M', label: 'Post-Study Work', icon: 'briefcase' },
    { value: '€45k+', label: 'Avg. Starting Salary', icon: 'trending' },
    { value: 'STEM', label: 'Top Ranking', icon: 'award' },
  ];

  readonly whyCards = [
    {
      title: 'Affordable Education',
      description: 'Public universities charge little to no tuition for international students.',
      image: 'affordable.jpg',
    },
    {
      title: 'Employability',
      description: 'Germany hosts Europe’s largest economy with strong industry partnerships.',
      image: 'corporate.jpg',
    },
    {
      title: 'Post-study Work',
      description: '18-month post-study residence permit to find employment in your field.',
      image: 'post_study.jpg',
    },
  ];

  readonly universities = [
    {
      name: 'TU Munich',
      location: 'Munich, Germany',
      type: 'Public',
      tags: ['QS #37', 'Engineering', 'Research'],
      image: 'uni-tum',
    },
    {
      name: 'RWTH Aachen',
      location: 'Aachen, Germany',
      type: 'Public',
      tags: ['QS #99', 'STEM', 'Industry Links'],
      image: 'uni-rwth',
    },
    {
      name: 'Heidelberg University',
      location: 'Heidelberg, Germany',
      type: 'Public',
      tags: ['QS #87', 'Medicine', 'Sciences'],
      image: 'uni-heidelberg',
    },
  ];

  readonly costItems = [
    {
      title: 'Tuition Fees',
      description: 'Most public universities charge €0–€1,500 per semester for international students.',
      icon: 'graduation',
    },
    {
      title: 'Living Expenses',
      description: 'Budget €850–€1,200 per month depending on city — Munich and Frankfurt run higher.',
      icon: 'home',
    },
    {
      title: 'Health Insurance',
      description: 'Mandatory public or private insurance, typically €110–€130 per month for students.',
      icon: 'shield',
    },
  ];

  readonly scholarships = [
    {
      title: 'DAAD Scholarship',
      description:
        'Germany’s largest funding organisation for international students — covers living costs, travel and tuition where applicable.',
      icon: 'globe',
    },
    {
      title: 'Erasmus+ Programme',
      description:
        'EU exchange funding for semester abroad, joint degrees and research placements at partner institutions.',
      icon: 'plane',
    },
  ];

  readonly visaSteps = [
    {
      step: '01',
      title: 'APS Certificate',
      description: 'Academic evaluation for students from select countries — verify your credentials early.',
    },
    {
      step: '02',
      title: 'Admission',
      description: 'Secure your university offer letter and complete enrolment documentation.',
    },
    {
      step: '03',
      title: 'Blocked Account',
      description: 'Open a blocked account with €11,208/year as proof of financial means.',
    },
    {
      step: '04',
      title: 'VFS Interview',
      description: 'Book your visa appointment, submit documents and attend the embassy interview.',
    },
  ];

  readonly faqs = [
    {
      question: 'Do I need to know German to study in Germany?',
      answer:
        'Many master’s programs are taught in English, but basic German helps with daily life, internships and long-term employment. We help you assess language requirements per program.',
    },
    {
      question: 'What is a blocked account and why is it required?',
      answer:
        'A blocked account (Sperrkonto) holds €11,208 per year as proof you can cover living costs. Funds are released monthly once you arrive in Germany.',
    },
    {
      question: 'How long does the German student visa process take?',
      answer:
        'Typically 6–12 weeks after your blocked account and admission letter are ready. APS processing can add 2–4 months — start early.',
    },
  ];

  readonly footerExplore = [
    { label: 'Countries', href: '#' },
    { label: 'Universities', href: '#universities' },
    { label: 'Scholarships', href: '#scholarships' },
    { label: 'Resources', href: '#' },
  ];

  readonly footerServices = [
    { label: 'Visa Support', href: '#visa' },
    { label: 'Insurance', href: '#costs' },
    { label: 'Blocked Account', href: '#costs' },
    { label: 'Counseling', href: '#inquiry' },
  ];

  readonly expandedFaq = signal<number | null>(0);

  /** Matches `/home1` final CTA form shape for the shared `h1-cta` section. */
  readonly form = {
    fullName: '',
    email: '',
    phone: '',
    destination: '',
  };

  toggleFaq(index: number): void {
    this.expandedFaq.update((current) => (current === index ? null : index));
  }

  /** Demo submit handler — same as `/home1`; not wired to the API. */
  onLeadSubmit(): void {
    // no-op; static benchmark page
  }

  ngOnInit(): void {
    this.title.setTitle(buildPageTitle('Study in Germany'));
  }
}
